import { useEffect, useRef, useState } from 'react';
import { Text, Button, Card, Subtitle1, Switch, } from '@fluentui/react-components';
import { CheckmarkCircleRegular, DismissRegular, ErrorCircleRegular, QuestionRegular } from "@fluentui/react-icons";
import {
    MessageBar,
    MessageBarActions,
    MessageBarTitle,
    MessageBarBody,
    MessageBarGroup,
    MessageBarIntent,
    makeStyles,
    shorthands,
} from "@fluentui/react-components";
import { PublicClientApplication, InteractionRequiredAuthError, AuthError, AuthenticationResult } from "@azure/msal-browser";

import { msalConfigGraph, loginRequestGraph } from '../authConfigGraph';
import { useSignInState } from '../signInState';
import authErrorHandling from '../authErrorHandling';
import useFetch from '../../Hooks/useFetchAPI';
import { FeatureSkeleton } from '../LoadingSkeletons';
import ModuleConfigTable from './ModuleConfigTable';
import { useConfig } from '../../context/ConfigContext';
import { isValidGuid } from '../../Helpers/utils';
import AdminLink from './HelperComponents/AdminLink';

interface FeatureConfigData {
    AppFeatureID: number;
    MSGraphEnabled: boolean;
    TenantID: string;
    AutoPatchEnabled: boolean;
    SyncSchedule1: string;
    Status: string;
    StatusMessage: string;
    MSGraphLastSyncEnd: Date | null;
    MSGraphLastSyncDuration: number | null;
}[];

const intents: MessageBarIntent[] = ["info", "warning", "error", "success"];

interface NotificationMessage {
    intent: MessageBarIntent;
    id: number;
    message: string;
};

const useStyles = makeStyles({
    messageBarGroup: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...shorthands.overflow("auto"),
    },
});


const ModuleConfigIntuneReporting = () => {
    const styles = useStyles();
    const { appName, knowledgeBaseUrl } = useConfig();
    const [authError, setauthError] = useState<boolean>(false);
    const [connectorTestError, setConnectorTestError] = useState<boolean>(false);
    const { signInState } = useSignInState();
    const featureName = 'IntuneReporting';
    const connectorName = 'IntuneEntra';
    const counterRef = useRef(0);

    const [featureConfigData, setFeatureConfigData] = useState<FeatureConfigData[] | null>(null);
    const [intuneReportingConfigItems, setIntuneReportingConfigItems] = useState<ModuleConfigTableProps[]>([]);
    const { fetchAPI: fetchConfig, apiData, isLoading, serverError } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: updateDatabase, apiData: submitData, isLoading: isLoadingSubmit, serverError: serverErrorSubmit } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: testConfig, apiData: testConfigData, isLoading: isLoadingTestConfig, serverError: serverErrorTestConfig } = useFetch({ scope: 'AdminUser' })
    const { fetchAPI: enableDataSync, apiData: enableDataSyncData, isLoading: isLoadingEnableDataSync, serverError: serverErrorEnableDataSync } = useFetch({ scope: 'AdminUser' })

    const [messages, setMessages] = useState<NotificationMessage[]>([]);

    const addMessage = (message: string, intent: MessageBarIntent) => {
        const newMessage = { intent, id: counterRef.current++, message };
        setMessages((s) => [newMessage, ...s]);
    };

    const dismissMessage = (messageId: number) =>
        setMessages((s) => s.filter((entry) => entry.id !== messageId)
        );


    useEffect(() => {
        const fetchConfigAsync = async () => {
            await fetchConfig({ url: 'admin/getfeatureconfig?featureName=IntuneReporting', method: 'GET' });
        };

        fetchConfigAsync()
    }, [signInState.currentCustomerID]);

    const msalInstanceGraph = new PublicClientApplication(msalConfigGraph(process.env.REACT_APP_CLIENT_GRAPH_APP_ID!));

    const handleAppRegConsent = async () => {
        await msalInstanceGraph.initialize();
        try {
            const response = await msalInstanceGraph.loginPopup(loginRequestGraph);
            // Handle the response
            setauthError(false);
        } catch (error) {
            // Handle the error
            console.log('[consentAppReg] Error:', error);
            setMessages([]);
            setauthError(true);
            addMessage(authErrorHandling(error), 'error');
        }
    };

    const updateConfiguration = async (key: string, value: string | number | boolean | Date) => {
        await updateDatabase({ url: `admin/updatefeatureconfig?featureName=${featureName}&${key}=${value}`, method: 'PUT' });

        setFeatureConfigData((prev) => {
            if (!prev) return prev; // Return unchanged if prev is falsy
            return prev.map(item =>
                item.hasOwnProperty(key) ? { ...item, [key]: value } : item
            );
        });
        setIntuneReportingConfigItems((prev) => {
            if (!prev) return prev;
            return prev.map(item =>
                item.key === key ? { ...item, value: value instanceof Date ? value.toISOString() : value } : item
            );
        });

        // Optionally re-fetch configuration if TenantID is updated
        if (key === 'TenantID') {
            await fetchConfig({ url: 'admin/getfeatureconfig?featureName=IntuneReporting', method: 'GET' });
        }

        //add feature config to the datasync db [admin].[SyncTenants] table
        if (key === 'MSGraphEnabled') {
            await enableDataSync({ url: 'admin/enabledatasync', method: 'POST', body: { MSGraphEnabled: value, } });
        }
    }

    useEffect(() => {
        if (featureConfigData) {
            testConfig({ url: `admin/testfeatureconfig?featureName=${featureName}&connectorName=${connectorName}`, method: 'GET' });
        }
    }, []);

    const testConfiguration = async () => {
        //todo: add more detailed test results for each configuration item (Graph, Log Analytics, AutoPatch, etc.)
        console.log('[ModuleConfig-Intune] Testing configuration');
        setMessages([]);
        setConnectorTestError(false);

        if (!featureConfigData?.[0]?.TenantID || !isValidGuid(featureConfigData[0].TenantID)) {
            setConnectorTestError(true);
            addMessage('The tenant id is not valid.', 'error');
            return;
        }

        await testConfig({ url: `admin/testfeatureconfig?featureName=${featureName}&connectorName=${connectorName}`, method: 'GET' });
    }

    useEffect(() => {
        if (testConfigData) {
            setMessages([]);
            (async () => {
                console.log('testResult:', testConfigData);
                if (apiData && Array.isArray(apiData.result) && apiData.result.some(item => !item.TenantID || item.TenantID.trim() === "")) {
                    console.log('Configuration test failed - No Tenant ID');
                    updateConfiguration('Status', 'Not Configured');
                    updateConfiguration('StatusMessage', 'Test Failed');
                    updateConfiguration('MSGraphEnabled', false);
                    setMessages([]);
                    setConnectorTestError(true);
                    addMessage("An Entra TenantID must be configured", 'warning');
                    return; // Stop further processing
                }
                if (testConfigData?.result) {
                    console.log('Configuration test successful');
                    updateConfiguration('Status', 'Configured');
                    updateConfiguration('StatusMessage', 'Tested Successfully');
                    setConnectorTestError(false);
                } else {
                    console.log('Configuration test failed');
                    updateConfiguration('Status', 'Not Configured');
                    updateConfiguration('StatusMessage', 'Test Failed');
                    updateConfiguration('MSGraphEnabled', false);
                    setMessages([]);
                    setConnectorTestError(true);
                    const errorString = typeof testConfigData.error === 'string' ? testConfigData.error : '';
                    const match = errorString.match(/Description: (.*?)(?= Trace ID:)/);
                    const errorMessage = match ? match[1].trim() : "Unknown error";
                    addMessage(errorMessage, 'error');
                }
            })();
        }
    }, [testConfigData]);

    useEffect(() => {
        if (!isLoading && !serverError && apiData) {
            console.log('[ModuleConfig-Intune] apiData', apiData)
            if (apiData && Array.isArray(apiData.result) && apiData.result.length > 0 && (apiData as unknown as { result: FeatureConfigData[] }).result) {
                let featureConfigDataResult: FeatureConfigData[] | null = null;
                let IntuneReportingConfigItemsResult: ModuleConfigTableProps[] = [];
                featureConfigDataResult = (apiData as unknown as { result: FeatureConfigData[] }).result;
                // console.log('featureConfigDataResult: ', featureConfigDataResult);
                setFeatureConfigData([...featureConfigDataResult]);

                if (Array.isArray(featureConfigDataResult)) {
                    IntuneReportingConfigItemsResult = featureConfigDataResult.flatMap(item => ([
                        { key: 'TenantID', displayName: 'Entra ID Tenant ID', value: item.TenantID },
                        // { key: 'AutoPatchEnabled', displayName: 'AutoPatch Enabled', value: item.AutoPatchEnabled },
                        // { key: 'SyncSchedule1', displayName: 'Connector Sync Time (UTC)', value: new Date(item.SyncSchedule1).toISOString().slice(11, 19) },
                    ]));
                }
                // console.log('IntuneReportingConfigItemsResult:', IntuneReportingConfigItemsResult);
                setIntuneReportingConfigItems([...IntuneReportingConfigItemsResult]);
            }
            else {
                console.log('[ModuleConfig-Intune] apiData is empty');
                let featureConfigDataResult: FeatureConfigData[] = [{
                    AppFeatureID: 1,
                    Status: '',
                    StatusMessage: '',
                    MSGraphEnabled: false,
                    TenantID: '',
                    AutoPatchEnabled: false,
                    SyncSchedule1: '',
                    MSGraphLastSyncEnd: null,
                    MSGraphLastSyncDuration: null,
                }];
                setFeatureConfigData(featureConfigDataResult);
                let IntuneReportingConfigItemsResult: ModuleConfigTableProps[] = [
                    { key: 'TenantID', displayName: 'Entra ID Tenant ID', value: '' },
                    // { key: 'AutoPatchEnabled', displayName: 'AutoPatch Enabled', value: false },
                    // { key: 'SyncSchedule1', displayName: 'Connector Sync Time (UTC)', value: new Date().toISOString().slice(11, 19) },
                ];
                setIntuneReportingConfigItems([...IntuneReportingConfigItemsResult]);

            }
        }
    }, [isLoading, serverError, apiData]);

    return (
        <div className="IntuneReportingConfig" style={{ margin: '20px', maxWidth: '1200px' }}>
            {isLoading && !serverError && !apiData &&
                <Card>
                    <Subtitle1>Intune & Entra Reporting Connector Setup</Subtitle1>
                    <FeatureSkeleton />
                </Card>

            }
            {!isLoading && !serverError && featureConfigData && apiData && intuneReportingConfigItems &&
                <Card>
                    <Subtitle1>Intune & Entra Reporting Connector Setup</Subtitle1>
                    <Text>An Entra ID Global Admin or Application Administrator will need to grant consent to the {appName}-GraphSync app to allow access to your data for Intune reporting.</Text>
                    <Text>Please see the following Information:</Text>
                    <ul>
                        <li>
                            <AdminLink href={knowledgeBaseUrl} target="_blank">
                                Connecting {appName} to Intune and Windows Update for Business Reports
                            </AdminLink>
                        </li>
                    </ul>
                    <Switch
                        key={"MSGraphEnabled"}
                        label={"Connector " + (featureConfigData[0].MSGraphEnabled ? "enabled" : "disabled")}
                        // labelPosition="before"
                        checked={featureConfigData[0].MSGraphEnabled}
                        disabled={!(
                            featureConfigData[0]?.TenantID &&
                            featureConfigData[0]?.StatusMessage === "Tested Successfully"
                        )}
                        onChange={(e) => updateConfiguration("MSGraphEnabled", e.target.checked)}
                    />

                    <Text>{featureConfigData[0]?.Status ? `Connector Status: ${featureConfigData[0]?.Status}` : null}</Text>
                    <Text>{featureConfigData[0]?.StatusMessage ? `Connector Status Message: ${featureConfigData[0]?.StatusMessage}` : null}</Text>
                    <Text>{featureConfigData[0]?.MSGraphLastSyncEnd ? `Connector Last Sync End Date: ${new Date(featureConfigData[0]?.MSGraphLastSyncEnd).toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : `Connector Last Sync End Date: N/A`}</Text>
                    <Text>
                        {featureConfigData[0]?.MSGraphLastSyncDuration == null
                            ? 'Connector Last Sync Duration: N/A'
                            : `Connector Last Sync Duration: ${featureConfigData[0]?.MSGraphLastSyncDuration === 0
                                ? '< 1 Min'
                                : `${featureConfigData[0]?.MSGraphLastSyncDuration} mins`
                            }`}
                    </Text>
                    < ModuleConfigTable<ModuleConfigTableProps[]> data={intuneReportingConfigItems} appFeature={'IntuneReporting'} onUpdate={updateConfiguration} />
                    <div style={{ minWidth: '50%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                        < Button style={{ width: '100%' }} onClick={handleAppRegConsent} appearance='primary'> Grant Consent </Button>
                    </div>
                    <div style={{ width: '50%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                        <Button style={{ width: '100%' }} onClick={testConfiguration}
                            icon={featureConfigData[0].StatusMessage === 'Tested Successfully'
                                ? <CheckmarkCircleRegular style={{ color: 'green' }} />
                                : <ErrorCircleRegular style={{ color: 'red' }} />}
                        >
                            Test Configuration
                        </Button>
                    </div>
                    {/* <br/> */}
                    {(authError || connectorTestError) &&
                        <MessageBarGroup className={styles.messageBarGroup}>
                            {messages.map(({ intent, id, message }) => (
                                <MessageBar key={`${intent}-${id}`} intent={intent}>
                                    <MessageBarBody>
                                        {authError && <MessageBarTitle>Error granting access:</MessageBarTitle>}
                                        {connectorTestError && <MessageBarTitle>Testing Error:</MessageBarTitle>}
                                        {message}
                                    </MessageBarBody>
                                    <MessageBarActions
                                        containerAction={
                                            <Button
                                                onClick={() => dismissMessage(id)}
                                                aria-label="dismiss"
                                                appearance="transparent"
                                                icon={<DismissRegular />}
                                            />
                                        }
                                    />
                                </MessageBar>
                            ))}
                        </MessageBarGroup>
                    }
                </Card>
            }
            {serverError &&
                // Put in nice pretty error handling component when it's created
                <>Server Error: {serverError}</>
            }

        </div>
    );

};

export default ModuleConfigIntuneReporting;
